var Keys = /*#__PURE__*/function (Keys) {
  Keys[Keys["Enter"] = 13] = "Enter";
  Keys[Keys["Escape"] = 27] = "Escape";
  Keys[Keys["Space"] = 32] = "Space";
  Keys[Keys["ArrowUp"] = 38] = "ArrowUp";
  Keys[Keys["ArrowDown"] = 40] = "ArrowDown";
  Keys[Keys["ArrowLeft"] = 37] = "ArrowLeft";
  Keys[Keys["ArrowRight"] = 39] = "ArrowRight";
  Keys[Keys["Tab"] = 9] = "Tab";
  Keys[Keys["Backspace"] = 8] = "Backspace";
  return Keys;
}(Keys || {});
/**
 * The `KeyboardNavigatable` class manages lists that can be navigated with the
 * keyboard, calling callbacks for each actions.
 *
 * This helper encapsulates the key binding logic, providing a simple fluent
 * API for use.
 */
export default class KeyboardNavigatable {
  /**
   * Callback to be executed for a specified input.
   */
  callbacks = new Map();

  /**
   * Callback that determines whether keyboard input should be handled.
   * By default, always handle keyboard navigation.
   */
  whenCallback = event => true;

  /**
   * Provide a callback to be executed when navigating upwards.
   *
   * This will be triggered by the Up key.
   */
  onUp(callback) {
    return this.onDirection(callback, Keys.ArrowUp);
  }

  /**
   * Provide a callback to be executed when navigating downwards.
   *
   * This will be triggered by the Down key.
   */
  onDown(callback) {
    return this.onDirection(callback, Keys.ArrowDown);
  }

  /**
   * Provide a callback to be executed when navigating leftwards.
   *
   * This will be triggered by the Left key.
   */
  onLeft(callback) {
    return this.onDirection(callback, Keys.ArrowLeft);
  }

  /**
   * Provide a callback to be executed when navigating rightwards.
   *
   * This will be triggered by the Right key.
   */
  onRight(callback) {
    return this.onDirection(callback, Keys.ArrowRight);
  }
  onDirection(callback, direction) {
    this.callbacks.set(direction, e => {
      e.preventDefault();
      callback(e);
    });
    return this;
  }

  /**
   * Provide a callback to be executed when the current item is selected..
   *
   * This will be triggered by the Return key (and Tab key, if not disabled).
   */
  onSelect(callback, ignoreTabPress = false) {
    const handler = e => {
      e.preventDefault();
      callback(e);
    };
    if (!ignoreTabPress) this.callbacks.set(Keys.Tab, handler);
    this.callbacks.set(Keys.Enter, handler);
    return this;
  }

  /**
   * Provide a callback to be executed when the current item is tabbed into.
   *
   * This will be triggered by the Tab key.
   */
  onTab(callback) {
    const handler = e => {
      e.preventDefault();
      callback(e);
    };
    this.callbacks.set(9, handler);
    return this;
  }

  /**
   * Provide a callback to be executed when the navigation is canceled.
   *
   * This will be triggered by the Escape key.
   */
  onCancel(callback) {
    this.callbacks.set(Keys.Escape, e => {
      e.stopPropagation();
      e.preventDefault();
      callback(e);
    });
    return this;
  }

  /**
   * Provide a callback to be executed when previous input is removed.
   *
   * This will be triggered by the Backspace key.
   */
  onRemove(callback) {
    this.callbacks.set(Keys.Backspace, e => {
      if (e instanceof KeyboardEvent && e.target instanceof HTMLInputElement && e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
        callback(e);
        e.preventDefault();
      }
    });
    return this;
  }

  /**
   * Provide a callback that determines whether keyboard input should be handled.
   */
  when(callback) {
    this.whenCallback = callback;
    return this;
  }

  /**
   * Set up the navigation key bindings on the given jQuery element.
   */
  bindTo($element) {
    // Handle navigation key events on the navigatable element.
    $element[0].addEventListener('keydown', this.navigate.bind(this));
  }

  /**
   * Interpret the given keyboard event as navigation commands.
   */
  navigate(event) {
    // This callback determines whether keyboard should be handled or ignored.
    if (!this.whenCallback(event)) return;
    const keyCallback = this.callbacks.get(event.which);
    if (keyCallback) {
      keyCallback(event);
    }
  }
}
flarum.reg.add('core', 'common/utils/KeyboardNavigatable', KeyboardNavigatable);